import styled from 'styled-components';
import getThemeByProps from '@userzoomlibraries/toolkit-theme-manager/get-theme-by-props';
import React, { useState, useCallback, useImperativeHandle } from 'react';
import PropTypes from '@userzoomlibraries/toolkit-js-utils/prop-types';
import IconButton from '@userzoomlibraries/toolkit-button/icon-button';
import useFocusable, { focusablePropTypes, focusableDefaultProps } from '@userzoomlibraries/toolkit-react-utils/use-focusable';
import useDataAttributes from '@userzoomlibraries/toolkit-react-utils/use-data-attributes';
import useAriaAttributes from '@userzoomlibraries/toolkit-react-utils/use-aria-attributes';
import noop from '@userzoomlibraries/toolkit-js-utils/noop';
import { jsx, jsxs } from 'react/jsx-runtime';

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var fallbackCloseIcon = /*#__PURE__*/jsx("svg", {
  viewBox: "0 0 14 14",
  version: "1.1",
  xmlns: "http://www.w3.org/2000/svg",
  xmlnsXlink: "http://www.w3.org/1999/xlink",
  role: "img",
  children: /*#__PURE__*/jsx("path", {
    d: "M2.05025 0.636033L6.99914 5.58514L11.9497 0.636033C12.3403 0.245509 12.9734 0.245509 13.364 0.636033C13.7545 1.02656 13.7545 1.65972 13.364 2.05025L8.41414 6.99914L13.364 11.9497C13.7545 12.3403 13.7545 12.9734 13.364 13.364C12.9734 13.7545 12.3403 13.7545 11.9497 13.364L6.99914 8.41414L2.05025 13.364C1.65972 13.7545 1.02656 13.7545 0.636033 13.364C0.245509 12.9734 0.245509 12.3403 0.636033 11.9497L5.58514 6.99914L0.636033 2.05025C0.245509 1.65972 0.245509 1.02656 0.636033 0.636033C1.02656 0.245509 1.65972 0.245509 2.05025 0.636033Z"
  })
}); // eslint-disable-next-line import/prefer-default-export

var _excluded = ["className", "closable", "children", "focusable", "onClose"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var Tag$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
      closable = _ref.closable,
      children = _ref.children,
      focusable = _ref.focusable,
      onClose = _ref.onClose,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  var ariaAttributes = useAriaAttributes(otherProps);
  var dataAttributes = useDataAttributes(otherProps);

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      tagNode = _useState2[0],
      setTagNode = _useState2[1];

  var focusableProps = useFocusable(tagNode, _objectSpread({
    focusable: focusable
  }, otherProps));
  var onClickHandler = useCallback(function () {
    if (!otherProps.disabled) {
      onClose();
    }
  }, [otherProps.disabled, onClose]);
  useImperativeHandle(ref, function () {
    return tagNode;
  });
  var classnames = ['gz-tag'];

  if (closable) {
    classnames.push('gz-tag--closable');
  }

  if (otherProps.disabled) {
    classnames.push('gz-tag--disabled');
  }

  if (focusable) {
    classnames.push('gz-tag--focusable');
  }

  classnames.push(className);
  return /*#__PURE__*/jsxs("span", _objectSpread(_objectSpread(_objectSpread(_objectSpread({
    ref: setTagNode,
    className: classnames.join(' ') // eslint-disable-next-line react/jsx-props-no-spreading

  }, ariaAttributes), dataAttributes), focusableProps), {}, {
    children: [/*#__PURE__*/jsx("span", {
      className: "gz-tag__content",
      children: children
    }), closable && /*#__PURE__*/jsx("span", {
      className: "gz-tag__close-button-wrapper",
      children: /*#__PURE__*/jsx(IconButton, {
        className: "gz-tag__close-button",
        disabled: otherProps.disabled,
        onClick: onClickHandler,
        name: "close",
        fallback: fallbackCloseIcon
      })
    })]
  }));
});
Tag$1.COMPONENT_ID = 'Gearz.Tag';
Tag$1.displayName = Tag$1.COMPONENT_ID;
Tag$1.propTypes = _objectSpread(_objectSpread({}, focusablePropTypes), {}, {
  className: PropTypes.stringWithDescription('Custom class to be applied.'),
  children: PropTypes.nodeWithDescription('Content of the tag.'),
  closable: PropTypes.boolWithDescription('Flag to add the close button to the tag.'),
  onClose: PropTypes.funcWithDescription('Callback called when the tag is closed.')
});
Tag$1.defaultProps = _objectSpread(_objectSpread({}, focusableDefaultProps), {}, {
  className: '',
  children: null,
  closable: false,
  onClose: noop
});

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Tag = styled(Tag$1).withConfig({
  componentId: "gz-tag-1673535304414__sc-1eklqhw-0"
})(["align-items:stretch;border:1px solid #000;border-radius:4px;display:inline-flex;justify-content:center;vertical-align:middle;.gz-tag__content{font-size:12px;line-height:20px;padding-left:6px;user-select:none;white-space:normal;word-break:break-word;}&:not([class*=\"gz-tag--closable\"]){.gz-tag__content{padding-right:6px;}}.gz-tag__close-button-wrapper{align-items:center;cursor:pointer;display:flex;justify-content:center;padding-right:6px;.gz-tag__close-button{align-items:center;background-color:transparent;border:none;cursor:cursor;display:flex;height:100%;justify-content:center;margin-left:6px;padding:0;position:relative;width:auto;.gz-icon{height:8px;width:8px;}}}&[aria-disabled=\"true\"]{cursor:not-allowed;opacity:0.48;user-select:none;.gz-tag__close-button-wrapper{cursor:not-allowed;.gz-tag__close-button{cursor:not-allowed;opacity:0.48;}}}", ""], function (props) {
  return getThemeByProps({
    componentId: Tag$1.COMPONENT_ID,
    props: props
  });
});
Tag.displayName = Tag$1.displayName;
Tag.propTypes = Tag$1.propTypes;
Tag.defaultProps = Tag$1.defaultProps;

export { Tag as T };
